/**
 * "Start with ELA or Math first" radio pair, rendered on the test-start
 * screens of BOTH the legacy fixed-form player (Module/index.tsx) and
 * the adaptive flow (AdaptiveFrames.tsx AdaptiveTestStart).
 *
 * The choice is NOT persisted (no DB field) — it only reorders the
 * in-memory ``modules`` array into flow order when the student presses
 * Start (see sectionOrder.ts). Default is ELA, matching the existing
 * behavior when the student never touches the picker.
 *
 * Uses the same radio glyphs as the MC choices (IoMdRadioButtonOn/Off)
 * so it reads as part of the existing SHSAT visual language; inline
 * styles only (matching PassageInterstitial's approach) — no new CSS.
 */

import React from 'react';
import { IoMdRadioButtonOn, IoMdRadioButtonOff } from 'react-icons/io';

import { StartSection } from './sectionOrder';

const OPTIONS: Array<{ value: StartSection; label: string }> = [
  { value: 'ela', label: 'English Language Arts' },
  { value: 'math', label: 'Mathematics' },
];

export function SectionStartPicker(props: {
  value: StartSection;
  onChange: (value: StartSection) => void;
}): JSX.Element {
  const { value, onChange } = props;
  return (
    <div
      role="radiogroup"
      aria-label="Choose which section to start with"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        margin: '4px 0 12px',
      }}
    >
      <div style={{ fontWeight: 600 }}>Start with:</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
        {OPTIONS.map((option) => (
          <div
            key={option.value}
            role="radio"
            aria-checked={value === option.value}
            tabIndex={0}
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onChange(option.value);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              fontWeight: value === option.value ? 600 : 400,
            }}
          >
            {value === option.value
              ? <IoMdRadioButtonOn style={{ color: '#2f6fd1', fontSize: '1.2em' }} />
              : <IoMdRadioButtonOff style={{ color: '#676767', fontSize: '1.2em' }} />}
            {option.label}
          </div>
        ))}
      </div>
    </div>
  );
}

export default SectionStartPicker;
